<template>
  <div>
    <b-sidebar
      id="sidebar-add-new-event"
      sidebar-class="sidebar-lg"
      :visible="isEventHandlerSidebarActive"
      bg-variant="white"
      width="800px"
      shadow
      backdrop
      no-header
      right
      @change="(val) => $emit('update:is-event-handler-sidebar-active', val)">
      <template #default="{ hide }">
        <!-- Header -->
        <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
          <h5 class="mb-0">
            {{ canCreateEvent ? eventLocal.id ? 'Alterar': 'Adicionar' : 'Confirmar' }} Evento
          </h5>
          <div>
            <feather-icon
              v-permission="'schedule_events.delete'"
              v-if="eventLocal.id"
              icon="TrashIcon"
              class="cursor-pointer"
              @click="$emit('remove-event', {calendar_id: eventLocal.id, schedule_id: eventLocal.extendedProps.scheduleEventId, hide: hide});"
            />
            <feather-icon
              class="ml-1 cursor-pointer"
              icon="XIcon"
              size="16"
              @click="hide"/>
          </div>
        </div>

        <div class="px-2 pt-1 flex flex-col gap-1" v-if="authorName || authorEmail">
          <label class="font">Agendado por</label>
          <div class="text-neutral flex flex-col border-grey border-solid border p-1 rounded">
            <span>
              {{ authorName }}
            </span>
            <span class="text-sm">
              {{ authorEmail }}
            </span>
          </div>
        </div>

        <!-- Body -->
        <validation-observer
          ref="refFormObserver">

          <!-- Form -->
          <b-form
            class="p-2"
            @submit.prevent="onSubmit"
            @reset.prevent="resetForm"
            autocomplete="off">

            <!-- Title -->
            <validation-provider
              #default="validationContext"
              :name="$t('names.title')"
              rules="required">
              <b-form-group
                label="Título"
                label-for="event-title">
                <b-form-input
                  id="event-title"
                  v-model="eventLocal.title"
                  autofocus
                  :state="getValidationState(validationContext)"
                  trim
                  placeholder="Título do evento"
                  :disabled="!canCreateEvent"/>

                <b-form-invalid-feedback>
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Tipo de evento -->
            <validation-provider
              #default="validationContext"
              :name="$t('names.calendar')"
              rules="required">

              <b-form-group
                label="Tipo de evento"
                label-for="calendar"
                :state="getValidationState(validationContext)">
                <b-form-radio-group
                  id="calendar-select"
                  v-model="eventLocal.extendedProps.calendar"
                  name="calendar"
                  :disabled="!canCreateEvent"
                  stacked>
                  <b-form-radio
                    v-for="calendarType in calendarTypes"
                    :key="calendarType.id"
                    :value="calendarType.id">
                    <div class="d-flex align-items-center">
                      <div
                        class="rounded-circle d-inline-block mr-2"
                        :style="`height:12px;width:12px;background-color: ${calendarType.color}`"/>
                      <span :style="`color: ${calendarType.color}`">{{ calendarType.name }}</span>
                    </div>
                  </b-form-radio>
                </b-form-radio-group>

                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>

            <!-- Event URL -->
            <validation-provider
              v-if="eventLocal.extendedProps.calendar !== 3"
              #default="validationContext"
              name="URL do Evento"
              rules="required|url">

              <b-form-group
                label="URL Evento"
                label-for="event-url">
                <div class="flex flex-row gap-1">
                  <b-form-input
                    class="flex-grow-0"
                    id="event-url"
                    v-model="eventLocal.extendedProps.eventUrl"
                    type="url"
                    :state="getValidationState(validationContext)"
                    placeholder=""
                    trim
                    :disabled="!canCreateEvent"/>
                  <b-button
                    v-if="event.extendedProps.eventUrl"
                    class="flex-grow w-1/6"
                    size="sm"
                    v-clipboard:copy="eventLocal.extendedProps.eventUrl"
                    v-clipboard:success="() => {
                      $toast('Copiado para a área de transferência!', {
                        type: 'success',
                        position: 'top-right',
                        duration: 3000
                      })
                    }"
                    v-clipboard:error="() => {
                      $toast('Não foi possível copiar para a área de transferência!', {
                        type: 'error',
                        position: 'top-right',
                        duration: 3000
                      })
                    }"
                    variant="outline-primary">
                    <b-icon-clipboard/>
                    <span v-if="!isSmallScreen()" class="text-base">Copiar</span>
                  </b-button>
                  <b-button
                    v-if="event.extendedProps.eventUrl"
                    class="flex items-center flex-grow w-1/6"
                    size="sm"
                    :href="eventLocal.extendedProps.eventUrl"
                    target="_blank"
                    variant="link">
                    <b-icon-link45deg/>
                    <span v-if="!isSmallScreen()" class="text-base">Abrir</span>
                  </b-button>
                </div>
                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                  {{ validationContext.errors[0] }}
                </b-form-invalid-feedback>
              </b-form-group>
            </validation-provider>


            <b-card
              border-variant="primary"
              title="Sugestões datas"
              bg-variant="transparent"
              class="shadow-none"
              id="divStartDate" ref="divStartDate">
              <b-card-text>
                <b-row v-if="canCreateEvent">
                  <b-col md="6">
                    <!-- Start Date -->
                    <validation-provider
                      #default="validationContext"
                      name="Start Date">

                      <b-form-group
                        label="Data Início"
                        label-for="start-date"
                        :state="getValidationState(validationContext)">
                        <flat-pickr
                          v-model="eventLocal.start_formatted"
                          class="form-control"
                          :config="config"/>
                        <b-form-invalid-feedback :state="getValidationState(validationContext)">
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>

                  <b-col md="6">
                    <!-- Duration -->
                    <validation-provider
                      #default="validationContext"
                      name="Duração"
                      rules="min:1|integer">
                      <b-form-group
                        label="Duração (Horas)"
                        label-for="duration"
                        :state="getValidationState(validationContext)">
                        <b-form-input id="duration" :state="(validationContext.validated ? validationContext.valid : null)" type="number" v-model="eventLocal.duration"/>
                        <b-form-invalid-feedback :state="getValidationState(validationContext)">
                          {{ validationContext.errors[0] }}
                        </b-form-invalid-feedback>
                      </b-form-group>
                    </validation-provider>
                  </b-col>
                </b-row>

                <b-row v-if="canCreateEvent">
                  <b-col>
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      class="float-right"
                      @click="addSuggestionDate">
                      Adicionar data
                    </b-button>
                  </b-col>
                </b-row>

                <b-row class="mt-2">
                  <b-col>
                    <date-suggestion-table
                      :show-guest-statistics="canCreateEvent"
                      :can-participate="userInvited"
                      :can-remove="canCreateEvent"
                      :items="suggestionDateList"
                      re-render-on-actions
                      @clicked-guest-statistics="openSuggestionGuestStatisticsModal"
                      @clicked-remove="removeSuggestionDate"
                      @clicked-confirm="confirmSchedule"
                      @clicked-disconfirm="disconfirmSchedule">
                      <template #after>
                        <div class="w-full border-grey border-solid border-l-0 border-r-0 border-b-0 border-t my-2"/>
                        <b-overlay
                          rounded
                          opacity="0.50">
                          <template #overlay>
                            <b>Não é possível editar este parâmetro.</b>
                          </template>
                          <div class="flex flex-row gap-2">
                            <b-form-group
                              label="Repetir"
                              label-for="repeat">
                              <b-form-select id="repeat" v-model="eventLocal.extendedProps.repeat">
                                <b-form-select-option :value="null">Nunca</b-form-select-option>
                                <b-form-select-option value="daily">Diariamente</b-form-select-option>
                                <b-form-select-option value="weekly">Semanalmente</b-form-select-option>
                                <b-form-select-option value="biweekly">Quinzenalmente</b-form-select-option>
                                <b-form-select-option value="monthly">Mensalmente</b-form-select-option>
                                <b-form-select-option value="yearly">Anualmente</b-form-select-option>
                              </b-form-select>
                            </b-form-group>
                            <validation-provider
                              #default="validationContext"
                              name="[Repetir] Até"
                            >
                              <b-form-group
                                v-if="eventLocal.extendedProps.repeat"
                                label="Até"
                                label-for="repeat_until"
                              >
                                <flat-pickr
                                  id="repeat_until"
                                  v-model="eventLocal.extendedProps.repeat_until"
                                  class="form-control"
                                  :config="config"
                                />
                                <b-form-invalid-feedback :state="getValidationState(validationContext)">
                                  {{ validationContext.errors[0] }}
                                </b-form-invalid-feedback>
                              </b-form-group>
                            </validation-provider>
                          </div>
                        </b-overlay>
                      </template>
                    </date-suggestion-table>
                  </b-col>
                </b-row>

              </b-card-text>
            </b-card>

            <b-card
              v-if="canCreateEvent"
              border-variant="primary"
              title="Lembretes"
              bg-variant="transparent"
              class="shadow-none">
              <b-card-text>
                <div class="reminder-items" :key="reminders_key">
                  <calendar-reminder-item
                    :key="i"
                    :reminder="reminder"
                    v-for="reminder, i in eventLocal.extendedProps.reminders"
                    :idx="i"
                    @input="updateReminder"
                    @remove="removeReminder"
                    :disabled="!canCreateEvent"
                  />
                  <calendar-reminder-item
                    v-if="eventLocal.extendedProps.reminders && eventLocal.extendedProps.reminders.length < 5 && canCreateEvent"
                    @input="addReminder"
                    :key="null"
                  />
                </div>
              </b-card-text>
            </b-card>

            <b-card
              border-variant="primary"
              title="Convidados"
              bg-variant="transparent"
              class="shadow-none"
              id="divGuest" ref="divGuest">
              <b-card-text>

                <b-form-group v-if="canCreateEvent">
                  <b-form-radio-group
                    v-model="userType"
                    aria-controls="ex-disabled-readonly">
                    <b-form-radio :value="'user'">
                      Usuário
                    </b-form-radio>
                    <b-form-radio :value="'legal_entity'">
                      Pessoa Jurídica
                    </b-form-radio>
                    <b-form-radio :value="'external_entity'">
                      Contato
                    </b-form-radio>
                  </b-form-radio-group>
                </b-form-group>

                <b-row :key="selectionKey" v-if="canCreateEvent">
                  <b-col v-if="canCreateEvent">
                    <!-- User Select -->
                    <select-suggestion
                      :key="selectedUser"
                      ref="userSelect"
                      v-if="userType == 'user'"
                      column="name,email"
                      model="User"
                      v-model="selectedUser"
                      label="Usuário"
                      :appendClearOption="true"
                      placeholderText="Digite o nome ou e-mail do usuário"
                      :itemTextFormat="
                        (model) => `${model.name} <br/><div class=\'text-sm\'>${model.email}<div>`
                      "/>
                    <!-- Legal Entity Select -->
                    <select-suggestion
                      :key="selectedUser"
                      ref="legalEntitySelect"
                      v-else-if="userType == 'legal_entity'"
                      column="company_name,trading_name,cnpj"
                      model="LegalEntity"
                      @selected-model="selectedLegalEntityGuest"
                      v-model="selectedLegalEntity"
                      label="Pessoa Jurídica"
                      :appendClearOption="true"
                      placeholderText="Digite o nome ou cnpj da pessoa jurídica"
                      :itemTextFormat="legalEntityItemTextFormat"/>

                    <div class="flex gap-2 grid grid-cols-2" v-else-if="userType == 'external_entity'">
                      <div>
                        <label for="entity_name">Nome</label>
                        <b-form-input
                          id="entity_name"
                          v-model="externalEntity.name"
                          type="text"
                          placeholder="Nome"/>
                      </div>
                      <div>
                        <label for="entity_email">Email</label>
                        <b-form-input
                          id="entity_email"
                          v-model="externalEntity.email"
                          type="email"
                          placeholder="Email"/>
                      </div>
                    </div>
                    <!-- Contact List Select -->
                    <div class="flex flex-row gap-4 items-end" v-if="userType == 'contact_list'">
                      <select-suggestion
                        class="w-full"
                        ref="contactListSuggestion"
                        column="name"
                        model="ContactList"
                        v-model="selectedContactList"
                        label="Lista de Contato"
                        :appendClearOption="true"
                        placeholderText="Digite o nome da lista."
                        :itemTextFormat="(val) => val.name"/>
                      <div v-if="selectedContactList"
                          class="flex-grow">
                        <b-button class="flex flex-row"
                          v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                          variant="danger"
                          @click="removeContactList(selectedContactList)"
                          v-tooltip="{
                            content: 'Retira do evento todos os convidados que fazem parte da lista selecionada.',
                            duration: {show: 100, hide: 100}
                          }">
                          <feather-icon
                            icon="TrashIcon"
                            class="mr-50"/>
                          <span class="align-middle">Remover</span>
                        </b-button>
                      </div>
                    </div>
                  </b-col>
                </b-row>
                <b-row align-v="end" :key="selectionKey" v-if="canCreateEvent">
                  <b-col class="flex gap-1 justify-end pt-1">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      class="px-1"
                      v-if="userType == 'external_entity'"
                      variant="primary"
                      @click="$bvModal.show('search-contact')">
                      <b-icon-search/>
                    </b-button>
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      @click="addGuest">
                      <feather-icon
                        icon="PlusIcon"
                        class="mr-50"/>
                      <span class="align-middle">Adicionar</span>
                    </b-button>
                  </b-col>
                </b-row>

                <b-row
                  align-v="end"
                  :key="selectionKey"
                  v-if="(
                    showOrganizationalUnit() &&
                    !isUserSelection &&
                    canCreateEvent &&
                    userType !== 'external_entity' &&
                    userType !== 'contact_list'
                  )"
                  class="mt-2">

                  <b-col md="9">
                    <b-form-group
                      label="Unidade Organizacional"
                      label-for="add-organization-units"
                      style="margin-bottom: unset;">
                      <v-select
                        v-model="selectedOrganizationalUnits"
                        multiple
                        :close-on-select="false"
                        :options="eventLocal.organizationalUnitsList"
                        label="label"
                        input-id="add-organization-units">
                        <div slot="no-options">Nenhuma unidade vinculada às instituições adicionadas!</div>
                      </v-select>
                    </b-form-group>
                  </b-col>

                  <b-col md="3">
                    <b-button
                      v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                      variant="primary"
                      class="float-right"
                      @click="addUnitGuest">
                      <feather-icon
                        icon="PlusIcon"
                        class="mr-50"/>
                      <span class="align-middle">Adicionar</span>
                    </b-button>
                  </b-col>
                </b-row>

                <b-row class="mt-2">
                  <b-col>
                    <guests-table
                      :guests="eventLocal.extendedProps.guests"
                      :show-actions="canCreateEvent"
                      @open-justifications="openJustificationsModal"
                      @remove-guest="removeGuest"
                    />
                  </b-col>
                </b-row>

              </b-card-text>
            </b-card>

            <h4>Descrição</h4>
            <simple-editor v-model="eventLocal.extendedProps.description" class="border-primary mb-2" :key="editorDescriptionKey" ref="refEditorDescription"/>

            <b-overlay blur="2px">
              <b-card
                border-variant="primary"
                title="Anexos"
                bg-variant="transparent"
                class="shadow-none">
                <b-card-text>
                  <schedule-event-attachments :key="attachmentRenderKey" :id="eventId" listed v-model="filesAdded">
                    <template #meta="dropzone">
                      {{ connectDropzone(dropzone) }}
                    </template>
                  </schedule-event-attachments>
                </b-card-text>
              </b-card>
              <template #overlay>
                <span class="font-bold">
                  Salve este agendamento para adicionar anexos.
                </span>
              </template>
            </b-overlay>

            <!-- Form Actions -->
            <div class="d-flex mt-2">
              <b-button
                v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                variant="primary"
                class="mr-2"
                type="submit"
                v-if="canCreateEvent">
                {{ eventLocal.id ? 'Atualizar' : 'Adicionar ' }}
              </b-button>
            </div>
          </b-form>
        </validation-observer>
      </template>
    </b-sidebar>
    <suggestion-guest-justifications
      :show.sync="showJustificationsPopup"
      :items="justificationItems"
      :guest="justificationGuest"/>
    <suggestion-guest-statistics
      :show.sync="showGuestStatisticsPopup"
      :schedule-suggestion="suggestionForStatistics"
      :event-guests="eventLocal.extendedProps.guests"
      @open-justifications="openJustificationsModal"
      :event="eventLocal"/>
    <calendar-contact-popup @selected="addContactListGuest"/>
  </div>
</template>

<script>

// Third Party; Libs and components
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormCheckbox,
  BFormSelect,
  BFormSelectOption,
  BAvatar,
  BFormTextarea,
  BButton,
  BFormInvalidFeedback,
  BRow,
  BCol,
  BTable,
  BCard,
  BCardText,
  BCardTitle,
  BFormRadioGroup,
  BFormRadio,
  BIconSearch,
  BIconLink45deg,
  BIconClipboard,
  BIconCheck,
  BIconX,
  BIconExclamationTriangle,
  BOverlay,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required, email, url, integer, min  } from '@validations'

// Utilities
import {getTextReadableTime} from '@/util/TextUtils'
import {emailValidate} from '@/util/Util'

// Composition
import { computed, reactive, ref, toRefs, watch } from '@vue/composition-api'
import formValidation from '@core/comp-functions/forms/form-validation'
import useCalendarEventHandler from './useCalendarEventHandler'

// Application Services
import ScheduleEventService from '@/services/api/ScheduleEventService'
import ScheduleGuestService from '@/services/api/ScheduleGuestService'

// Application Components - Specifics
import SuggestionGuestJustifications from './SuggestionGuestJustifications.vue'
import SuggestionGuestStatistics from './SuggestionGuestStatistics.vue'
import GuestsTable from './GuestsTable.vue'
import CalendarReminderItem from './CalendarReminderItem.vue'
import DateSuggestionTable from './DateSuggestionTable.vue'

// Application Components - General
import SelectSuggestion from '@/components/SelectSuggestion.vue'
import SimpleEditor from '@/components/SimpleEditor.vue'
import ScheduleEventAttachments from '../../schedules/ScheduleEventAttachmentsInput.vue'
import CalendarContactPopup from './CalendarContactPopup.vue'

export default {
  components: {
    BButton,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormCheckbox,
    BFormTextarea,
    BFormSelect,
    BFormSelectOption,
    BAvatar,
    vSelect,
    flatPickr,
    ValidationProvider,
    BFormInvalidFeedback,
    ValidationObserver,
    BRow,
    BCol,
    BTable,
    BCard,
    BCardText,
    BCardTitle,
    BFormRadioGroup,
    BFormRadio,
    SelectSuggestion,
    CalendarReminderItem,
    BIconLink45deg,
    BIconClipboard,
    BIconCheck,
    BIconX,
    BIconExclamationTriangle,
    BIconSearch,
    SuggestionGuestJustifications,
    GuestsTable,
    SuggestionGuestStatistics,
    SimpleEditor,
    DateSuggestionTable,
    ScheduleEventAttachments,
    BOverlay,
    CalendarContactPopup,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: 'isEventHandlerSidebarActive',
    event: 'update:is-event-handler-sidebar-active',
  },
  props: {
    isEventHandlerSidebarActive: {
      type: Boolean,
      required: true,
    },
    event: {
      type: Object,
      required: true,
    },
    clearEventData: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      required,
      email,
      url,
      integer,
      min,
      fieldsGuests: [],
      calendarTypes: [],
      suggestionDateListKey: 0,
      reminders_key: 0,
      editorDescriptionKey: 0,
      showJustificationsPopup: false,
      justificationItems: [],
      justificationGuest: null,
      suggestionForStatistics: null,
      showGuestStatisticsPopup: false,
      selectedContactList: null,
      dropzoneConnector: null,
      filesAdded: [],
      repeat: null,
      repeat_until: null
    }
  },
  computed: {
    creating() {
      return !this.eventLocal.id
    },
    authorName() {
      return this.event.extendedProps.author_name
    },
    authorEmail() {
      return this.event.extendedProps.author_email
    },
    eventUrl() {
      return this.eventLocal.extendedProps.url
    },
    getLocale() {
      return this.$i18n.locale
    },
    config() {
      return {
        enableTime: true,
        dateFormat: 'd/m/Y H:i',
        locale: require(`flatpickr/dist/l10n/${this.getLocale}.js`).default[this.getLocale]
      }
    },
    canCreateEvent() {
      return this.$acl.hasAnyPermission('schedule_events.create')
    },
    suggestionDateList() {
      return this.eventLocal
        && this.eventLocal.extendedProps
          && this.eventLocal.extendedProps.suggestionDateList ? this.eventLocal.extendedProps.suggestionDateList : []
    },
    eventId() {
      const id = this.event && this.event.extendedProps && this.event.extendedProps.scheduleEventId
      return id
    }
  },
  watch: {
    '$store.state.calendar.calendarTypes'(val) {
      this.calendarTypes = val
    },
    showJustificationsPopup(val) {
      if (!val) {
        this.justificationItems = []
        this.justificationGuest = null
      }
    },
    filesAdded(files) {
      if (this._.isArray(files) && files.length > 0) {
        this.$store.dispatch('calendar/setInputFiles', files)
      }
    },
    isEventHandlerSidebarActive(val) {
      if (!val) {
        this.filesAdded = []
        this.$store.dispatch('calendar/setInputFiles', [])
      }
    },
    'eventLocal.extendedProps.description': {
            handler: function (newVal, oldVal) {
                // Changes detected. Do work...
                //onsole.log('CHANGE DESCRIPTION....')
                if(this._.isEmpty(newVal)){ //Fix clear description
                  this.clearEditorDescription()
                }
                //this.$nextTick(() => {
                  //this.editorDescriptionKey++
                //})
            },
            deep: true,
            immediate: true
        }
  },
  methods: {
    connectDropzone(dropzone) {
      this.dropzoneConnector = dropzone
    },
    openSuggestionGuestStatisticsModal(data) {
      this.showGuestStatisticsPopup = true
      this.suggestionForStatistics = data
    },
    openJustificationsModal(guest) {
      this.justificationItems = guest.justifications
      this.justificationGuest = guest
      this.showJustificationsPopup = true
    },
    showOrganizationalUnit() {
      return this.eventLocal.organizationalUnitsList ? this.eventLocal.organizationalUnitsList.length > 0 : false
    },
    updateReminder(idx, payload) {
      console.log("Updating Reminder", idx, payload)

      const old = this.eventLocal.extendedProps.reminders[idx]
      this.eventLocal.extendedProps.reminders[idx] = {...old, ...payload}

      this.$nextTick(() => {
        this.reminders_key++
      })
    },
    addReminder(idx, payload) {
      console.log("Adding Reminder", idx, payload)

      this.eventLocal.extendedProps.reminders.push(payload)
    },
    removeReminder(idx, ) {
      console.log("Removing Reminder", idx)
      this.eventLocal.extendedProps.reminders.splice(idx, 1)
    },
    findGuestPosition(guest_id) {
      return this.eventLocal.extendedProps.guests.findIndex(g => g.id === guest_id)
    },
    setScheduleGuestAsConfirmed(confirmScheduleResponse) {
      const guest_id = this._.get(confirmScheduleResponse, 'schedule_suggestion_guest.id')

      const guestPosition = this.findGuestPosition(guest_id)

      if (guestPosition !== -1) {
        this.eventLocal.extendedProps.guests[guestPosition].confirmed = true
      }
    },
    setScheduleGuestAsNotConfirmed(disconfirmScheduleResponse) {

      // Verify if other suggestions have been confirmed.

      const isScheduled = (suggestion) => !!suggestion.scheduled
      const hasOtherScheduledSuggestion = (
        this.eventLocal.extendedProps.suggestionDateList
      ).some(isScheduled)

      if (hasOtherScheduledSuggestion) return

      /**
       * If no other schedule suggestion has been confirmed,
       * We search the where the unconfirmed guest is and
       * set it as not confirmed.
       */

      const guest_id = this._.get(disconfirmScheduleResponse, 'suggestion_guest_id')

      const guestPosition = this.findGuestPosition(guest_id)

      if (guestPosition !== -1) {
        this.eventLocal.extendedProps.guests[guestPosition].confirmed = false
      }
    },

    confirmSchedule(suggestion, index) {
      this.scheduleGuestService.confirmSchedule(suggestion.id, {
        schedule_event_id: suggestion.schedule_event_id
      }).then(response => {
        suggestion.scheduled = true
        this.updateLocalSuggestionData(index, {scheduled: true})
        this.setScheduleGuestAsConfirmed(response)
        this.suggestionDateListKey++

        this.notifySuccess(this.$vs, 'Confirmação realizada com sucesso!')

        this.$emit('change-confirmation', true)
      })
    },
    disconfirmSchedule(suggestion, index) {
      this.scheduleGuestService.disconfirmSchedule(suggestion.id, {
        schedule_event_id: suggestion.schedule_event_id
      }).then(response => {
        suggestion.scheduled = false
        this.updateLocalSuggestionData(index, {scheduled: false})
        this.setScheduleGuestAsNotConfirmed(response)
        this.suggestionDateListKey++

        this.notifySuccess(this.$vs, 'Agendamento desmarcado com sucesso!')

        this.$emit('change-confirmation', false)
      })
    },
    addSuggestionDate() {
      const form_start_at = this.eventLocal.start_formatted
      const duration = parseInt(this.eventLocal.duration)

      if (!(this._.isNumber(duration) && duration > 0)) {
        this.notifyError(this.$vs, "Duração não pode ser menor ou igual a zero horas.")
        return
      }

      const end_at = this.$moment(form_start_at, 'DD-MM-YYYY HH:mm');
      end_at.add(duration, 'hours')

      const form_end_at = end_at.format('DD/MM/YYYY HH:mm')

      console.log('our start', form_start_at, 'our ending', form_end_at)

      if (!form_start_at || !form_end_at) {
        this.notifyWarning(this.$vs, "Não é possível adicionar sugestão de agendamento sem uma data de ínicio ou fim.")
        return
      }

      if (form_start_at && form_end_at) {
        const startAt = this.$moment(form_start_at, 'DD/MM/YYYY HH:mm')
        const endAt = this.$moment(form_end_at, 'DD/MM/YYYY HH:mm')

        if (endAt.isBefore(startAt)) {
          this.notifyWarning(this.$vs, "Não é possível adicionar sugestão de agendamento com data de ínicio maior que data de fim.")
          return
        }
      }

      const exists = this.eventLocal.extendedProps.suggestionDateList.find(element => {
        return ((element.start_at_formatted === form_start_at) && (element.finish_at_formatted === form_end_at))
      })

      if (exists) {
        this.notifyWarning(this.$vs, "Já consta a Data/Hora selecionada!", 3000)
        return
      }

      this.eventLocal.extendedProps.suggestionDateList.push({
        start_at_formatted: this.eventLocal.start_formatted,
        finish_at_formatted: form_end_at,
        start_at: this.eventLocal.start_formatted,
        finish_at: form_end_at,
      })
    },
    removeSuggestionDate(item) {
      this.eventLocal.extendedProps.suggestionDateList = this.eventLocal.extendedProps.suggestionDateList.filter(element => {
        return !(element.start_at === item.start_at && element.finish_at === item.finish_at)
      })
    },
    legalEntityItemTextFormat(model){
      let name = `${model.company_name} - ${model.trading_name}`
      const hasCnpj = !!model.cnpj
      if (hasCnpj) {
        name = `${name} ( ${this.cnpjFormat(model.cnpj)}) `
      }

      return name
    },
    loadOrganizationalUnits(legalEntity) {

      if (legalEntity.organizational_units && legalEntity.organizational_units.length > 0 && this._.isArray(legalEntity.organizational_units)) {
        const units = legalEntity.organizational_units.map((unit) => {
          return {
            'label': unit.name,
            'name': unit.name,
            'id': unit.id
          }
        })
        const previousUnits = this.eventLocal.organizationalUnitsList || []

        this.eventLocal.organizationalUnitsList = this._.unionBy(units, previousUnits, 'id')
      }
    },
    addUserGuest() {
      let model = {}
      if (this.selectedUser) {
        model = this.selectedUser
        model.type = 'user'

        const pushed = this.pushGuestToList(model)
        this.selectedUser = null
        if (!pushed) return null
      }
    },
    selectedLegalEntityGuest(model) {
      this.eventLocal.organizationalUnitsList = []
      this.loadOrganizationalUnits(model)
    },
    clearLegalEntity() {
      const select = this.$refs.legalEntitySelect
      if (select) select.reset()
      this.selectedLegalEntity = null
    },
    addLegalEntityGuest() {
      let model = {}
      if (this.selectedLegalEntity) {
        model = this.selectedLegalEntity
        model.type = 'legal_entity'

        const pushed = this.pushGuestToList(model)
        this.clearLegalEntity()
        if (!pushed) return null
      }
    },
    clearContactList() {
      const select = this.$refs.contactListSuggestion
      if (select) select.reset()
      this.selectedContactList = null
    },
    removeContactList(list) {
      const contactEmails = list.contacts.map((c) => c.email)
      this.eventLocal.extendedProps.guests = this.eventLocal.extendedProps.guests.filter(
        (guest) => {
          let partOfList = contactEmails.some((email) => guest.email === email)
          return !partOfList
        }
      )
    },
    addSingleExternalEntityGuest(entity) {
      entity.type = 'external_entity'
      this.pushGuestToList(entity)
    },
    addExternalEntityGuest(){
      let model = {}
      if (this.externalEntity && this.externalEntity.name && this.externalEntity.email) {
        if(!emailValidate(this.externalEntity.email)){
          this.notifyWarning(this.$vs,'E-mail do convidado está incorreto!',3000)
          return
        }
        model = this._.cloneDeep(this.externalEntity)
        model.type = 'external_entity'

        this.pushGuestToList(model)

        this.externalEntity = reactive({
          name: '',
          email: ''
        })
      }else{
        this.notifyWarning(this.$vs,'Preencha/Selecione corretamente os campos do convidado!',3000)
      }
    },
    addContactListGuest(contactList = null){
      const list = contactList || this.selectedContactList
      if (list) {
        if (this._.isArray(list) && list.length > 0) {
          list.forEach(
            (contact) => this.addSingleExternalEntityGuest({name: contact.name, email: contact.email})
          )
          this.clearContactList()
        }
      }
    },
    addGuest() {
      switch (this.userType) {
      case 'user':
        return this.addUserGuest()
      case 'legal_entity':
        return this.addLegalEntityGuest()
      case 'external_entity':
        return this.addExternalEntityGuest()
      case 'contact_list':
        return this.addContactListGuest()
      default:
        console.error('Unknown guest type.')
        break
      }
    },
    isSameGuest(guestA, guestB) {
      let sameEntity = false
        if (guestA.type === 'external_entity' && guestB.type === guestA.type) {
          sameEntity = guestB.email === guestA.email
        } else {
          const sameId = (guestB.entity_id || guestB.id) == (guestA.entity_id || guestA.id)
          const sameType = guestB.type == guestA.type
          sameEntity = !!(sameId && sameType)
        }
        return sameEntity
    },
    pushGuestToList(model) {

      if (!model) return false

      const exists = this.eventLocal.extendedProps.guests.findIndex((guest) => {
        return this.isSameGuest(model, guest)
      })

      if (exists !== -1) {
        let message = "Este convidado já está adicionado."
        if (model.type === 'external_entity') {
          message = `O convidado ${model.email} já está adicionado.`
        }
        this.notifyWarning(this.$vs, message, getTextReadableTime(message, 500))
        return false
      }

      this.eventLocal.extendedProps.guests.push(model)
      return true
    },
    addUnitGuest() {
      this.selectedOrganizationalUnits.forEach(element => {
        element.type = 'organizational_unit'
      })

      const units = this.selectedOrganizationalUnits

      if (this._.isArray(units) && units.length > 0) {
        units.forEach((unit) => {
          this.pushGuestToList(unit)
        })
      }
    },
    removeGuest(guest) {
      this.eventLocal.extendedProps.guests = this.eventLocal.extendedProps.guests.filter(element => {
        return !this.isSameGuest(guest, element)
      })
    },
    onSubmit() {
      console.log('on Submit event...')
      this.$refs.refFormObserver.validate().then((success) => {

        if (success) {
          let successValidate = this.validateFieldsSchedule()
          if(successValidate){
            console.log('on Submit event sucess...')
            const eventData = JSON.parse(JSON.stringify(this.eventLocal))

            // * If event has id => Edit Event
            // Emit event for add/update event
            if (this.event && this.event.id) {
              this.$emit('update-event', eventData)
            } else {
              this.$emit('add-event', eventData)
              // Close sidebar
              //this.$emit('update:is-event-handler-sidebar-active', false)
            }
          }
        }else{
          console.log('on Submit event error...')
          this.errorAlertFormValidaded()
        }
      })
    },
    errorAlertFormValidaded() {
      const firstField = this.$el.querySelector('.is-invalid')
      if (firstField) {
        firstField.scrollIntoView()
      }
      this.notifyWarning(
        this.$vs,
        'Preencha corretamente os campos do agendamento!',
        3000
      )
    },
    validateFieldsSchedule(){
      //Sugestion date
      if(this.suggestionDateList && this.suggestionDateList.length === 0){
        this.notifyWarning(
        this.$vs,
        'Adicione ao menos uma data/hora do agendamento!',
        3000)
        const divSuggestion = this.$refs.divStartDate
        divSuggestion ?  divSuggestion.scrollIntoView() : this.$el.querySelector('.is-invalid').scrollIntoView()
        return false
      }//

      //Guest
      let guestLength = this.eventLocal.extendedProps.guests.length
      if(guestLength === 0){
        this.notifyWarning(
        this.$vs,
        'Adicione ao menos um convitado para o agendamento!',
        3000)
        const divGuest = this.$refs.divGuest
        divGuest ?  divGuest.scrollIntoView() : this.$el.querySelector('.is-invalid').scrollIntoView()
        return false
      }//

      //validated
      return true
    },
    isScheduled(data) {
      if (!this.isEmpty(data.item.scheduled)) {
        return data.item.scheduled
      }
      return data.item.guests && data.item.guests.length > 0
    },
    userInvited() {
      return this._.get(this.eventLocal, 'extendedProps.invited', false)
    },
    updateLocalSuggestionData(index, data)
    {
      this.$emit('update-suggestion', index, data)
    },
    clearEditorDescription(){
      if(this.$refs && this.$refs.refEditorDescription){
        this.$refs.refEditorDescription.clearEditor()
      }
    },
    resetForm() {
      this.$refs.refFormObserver.reset()
      this.eventLocal = this._.cloneDeep(this.event)
      this.filesAdded = []
      this.$store.dispatch('calendar/setInputFiles', [])
    },
  },
  mounted() {
    this.$nextTick(() => {
      if (this.creating) {
        const moment = this.$moment()
        moment.add(1, 'month')
        this.eventLocal.extendedProps.repeat_until = moment.format('DD/MM/YYYY HH:mm')
      } else {
        // const moment = this.$moment(this.eventLocal.extendedProps.repeat_until)
        // this.eventLocal.extendedProps.repeat_until = moment.format('DD/MM/YYYY HH:mm')
      }
      this.calendarTypes = this.$store.state.calendar.calendarTypes
    })
  },
  setup(props, { emit, root }) {

    const clearFormData = ref(null)

    const {
      eventLocal,
      resetEventLocal,
      calendarOptions,
    } = useCalendarEventHandler(toRefs(props), clearFormData, emit)

    const {
      refFormObserver,
      getValidationState,
      resetForm,
      clearForm,
    } = formValidation(resetEventLocal, props.clearEventData)

    const guestsOptions = []

    const scheduleEventService = ScheduleEventService.build(root.$vs)
    const scheduleGuestService = ScheduleGuestService.build(root.$vs)
    const selectedUser = ref(null)
    const selectedLegalEntity = ref(null)
    const selectedOrganizationalUnits = ref(null)
    const organizationalUnits = []
    const userType = ref('user')
    const isUserSelection  = computed(() => {
      const type = userType.value
      return type == 'user'
    })
    const selectionKey = ref(0)

    const externalEntity = reactive({
      name: '',
      email: ''
    })


    watch(isUserSelection, () => {
      selectionKey.value += 1
    })

    clearFormData.value = clearForm
    return {
      // Add New Event
      eventLocal,
      calendarOptions,
      guestsOptions,

      // Form Validation
      resetForm,
      refFormObserver,
      getValidationState,

      // Services
      scheduleEventService,
      scheduleGuestService,

      // Local data
      selectedUser,
      selectedLegalEntity,
      selectedOrganizationalUnits,
      organizationalUnits,
      userType,
      externalEntity,
      isUserSelection,
      selectionKey,
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.reminder-items {
  display: flex;
  @apply flex-col gap-1;
}

#calendar-select .vs__dropdown-option--highlight {
  background: rgba(var(--vs-primary), 0.05) !important;
}

#calendar-select .vs__dropdown-option--selected{
  background-color: rgba(var(--vs-primary), 0.2) !important;
}
</style>
